import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "backdrop", "panel", "step", "stepIndicator"]
  static values = {
    currentStep: { type: Number, default: 1 },
    newAsanas: { type: Boolean, default: false }
  }

  connect() {
    this.answers = {
      body_feel: null,
      intensity: null,
      new_asanas: null,
      new_asanas_result: null,
      notes: ""
    }
  }

  open() {
    this.currentStepValue = 1
    this._resetSelections()
    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    requestAnimationFrame(() => {
      this.backdropTarget.classList.add("opacity-100")
      this.backdropTarget.classList.remove("opacity-0")
      this.panelTarget.classList.add("survey-enter")
    })
    this._showStep(1)
  }

  close() {
    this.backdropTarget.classList.add("opacity-0")
    this.backdropTarget.classList.remove("opacity-100")
    this.panelTarget.classList.remove("survey-enter")
    setTimeout(() => {
      this.modalTarget.classList.add("hidden")
      document.body.style.overflow = ""
    }, 300)
  }

  selectOption(event) {
    const { field, value } = event.currentTarget.dataset

    // Store the answer
    if (field === "intensity") {
      this.answers[field] = parseInt(value)
    } else if (field === "new_asanas") {
      this.answers[field] = value === "true"
      this.newAsanasValue = value === "true"
    } else {
      this.answers[field] = value
    }

    // Visual feedback: highlight selected, un-highlight siblings
    const step = event.currentTarget.closest("[data-step]")
    step.querySelectorAll(`[data-field="${field}"]`).forEach(el => {
      const isSelected = el === event.currentTarget
      el.classList.toggle("ring-2", isSelected)
      el.classList.toggle("ring-indigo-500", isSelected)
      el.classList.toggle("bg-indigo-50", isSelected)
      el.classList.toggle("border-indigo-200", isSelected)
      if (!isSelected) {
        el.classList.remove("ring-2", "ring-indigo-500", "bg-indigo-50", "border-indigo-200")
      }
    })
  }

  updateNotes(event) {
    this.answers.notes = event.target.value
  }

  next() {
    let nextStep = this.currentStepValue + 1

    // Step 3 -> conditional: if new_asanas is true, show 3.5; otherwise skip to 4
    if (this.currentStepValue === 3) {
      nextStep = this.newAsanasValue ? 3.5 : 4
    }
    // Step 3.5 -> 4
    if (this.currentStepValue === 3.5) {
      nextStep = 4
    }

    // Past last step: submit
    if (nextStep > 4) {
      this._submit()
      return
    }

    this.currentStepValue = nextStep
    this._showStep(nextStep)
  }

  back() {
    let prevStep = this.currentStepValue - 1

    // Going back from step 4: if newAsanas was yes, go to 3.5; else go to 3
    if (this.currentStepValue === 4) {
      prevStep = this.newAsanasValue ? 3.5 : 3
    }
    // Going back from 3.5 goes to 3
    if (this.currentStepValue === 3.5) {
      prevStep = 3
    }

    if (prevStep < 1) return

    this.currentStepValue = prevStep
    this._showStep(prevStep)
  }

  skip() {
    // Clear the answer for the current step's field
    const stepEl = this._currentStepElement()
    if (stepEl) {
      const field = stepEl.dataset.field
      if (field) {
        this.answers[field] = null
        if (field === "new_asanas") this.newAsanasValue = false
      }
      // Un-highlight any selected option
      stepEl.querySelectorAll("[data-field]").forEach(el => {
        el.classList.remove("ring-2", "ring-indigo-500", "bg-indigo-50", "border-indigo-200")
      })
    }
    this.next()
  }

  skipAll() {
    this.answers = {
      body_feel: null,
      intensity: null,
      new_asanas: null,
      new_asanas_result: null,
      notes: ""
    }
    this._submit()
  }

  // Private

  _showStep(step) {
    this.stepTargets.forEach(el => {
      const stepNum = parseFloat(el.dataset.step)
      const isMatch = stepNum === step
      el.classList.toggle("hidden", !isMatch)
      if (isMatch) {
        el.classList.add("survey-step-enter")
        setTimeout(() => el.classList.remove("survey-step-enter"), 200)
      }
    })
    this._updateIndicator(step)
  }

  _currentStepElement() {
    return this.stepTargets.find(
      el => parseFloat(el.dataset.step) === this.currentStepValue
    )
  }

  _updateIndicator(step) {
    // Map: 1->0, 2->1, 3->2, 3.5->2, 4->3
    const indicatorIndex = step <= 3 ? step - 1 : (step === 3.5 ? 2 : 3)
    this.stepIndicatorTargets.forEach((dot, i) => {
      dot.classList.toggle("bg-indigo-500", i <= indicatorIndex)
      dot.classList.toggle("bg-gray-200", i > indicatorIndex)
    })
  }

  _resetSelections() {
    this.answers = {
      body_feel: null,
      intensity: null,
      new_asanas: null,
      new_asanas_result: null,
      notes: ""
    }
    this.newAsanasValue = false

    // Clear visual selections
    this.stepTargets.forEach(step => {
      step.querySelectorAll("[data-field]").forEach(el => {
        el.classList.remove("ring-2", "ring-indigo-500", "bg-indigo-50", "border-indigo-200")
      })
    })

    // Clear notes textarea
    const textarea = this.element.querySelector("textarea")
    if (textarea) textarea.value = ""
  }

  _submit() {
    const form = document.getElementById("practice-log-form")
    if (!form) return

    // Remove any previously injected survey inputs
    form.querySelectorAll("[data-survey-field]").forEach(el => el.remove())

    const fields = {
      "practice_log[survey_body_feel]": this.answers.body_feel,
      "practice_log[survey_intensity]": this.answers.intensity,
      "practice_log[survey_new_asanas]": this.answers.new_asanas,
      "practice_log[survey_new_asanas_result]": this.answers.new_asanas_result,
      "practice_log[survey_notes]": this.answers.notes
    }

    for (const [name, value] of Object.entries(fields)) {
      if (value !== null && value !== undefined && value !== "") {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = name
        input.value = value
        input.dataset.surveyField = "true"
        form.appendChild(input)
      }
    }

    this.close()

    // Small delay to let the close animation start, then submit
    setTimeout(() => {
      form.requestSubmit()
    }, 50)
  }
}
